/**
 * EditScenariosChangeReviewCard body (Gate 2).
 *
 * v0: Excel-download review surface. The user downloads the
 * initial-vs-modified diff XLSX, reviews the highlighted changes,
 * then Confirms (advance to sandbox refresh + recalculation) or
 * Amends (re-opens Gate 1 to revise parameters).
 *
 * changes_log is on the card payload but NOT rendered in v0 — the
 * XLSX is the review surface. Future iterations may add inline
 * per-ChangeRecord rendering for quicker review without downloading.
 *
 * Confirm/Cancel/Amend buttons are owned by CardShell's footer.
 * This body is purely the download affordance.
 *
 * Styling matches v2 card conventions: MUI theme tokens, same density
 * as other card bodies (not a custom blue-accented download card style).
 */
import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

interface EditScenariosChangeReviewBodyProps {
  content: Record<string, unknown>;
  onEditField: (field_updates: Record<string, unknown>) => void;
  editsDisabled: boolean;
}

export const EditScenariosChangeReviewBody: React.FC<
  EditScenariosChangeReviewBodyProps
> = ({ content }) => {
  const diffUrl =
    typeof content.diff_xlsx_url === 'string' ? content.diff_xlsx_url : null;

  return (
    <Stack spacing={1.5}>
      {diffUrl ? (
        <DiffDownloadCard url={diffUrl} />
      ) : (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Diff comparison file is not available.
        </Typography>
      )}
    </Stack>
  );
};

interface DiffDownloadCardProps {
  url: string;
}

const DiffDownloadCard: React.FC<DiffDownloadCardProps> = ({ url }) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
          Review proposed changes
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}
        >
          Download the Excel comparison file to review the proposed changes
          before confirming.
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
          Link expires in ~1 hour.
        </Typography>
      </Box>

      <Button
        size="small"
        variant="outlined"
        endIcon={<CloudDownloadIcon />}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Download
      </Button>
    </Box>
  );
};
